import { type Inventory, type Invoice, type Payment } from "@/core";
import { timestampToDate } from "@/utils/formatting";

export type AttentionState =
  | "Ready"
  | "Needs Action"
  | "Awaiting Approval"
  | "At Risk"
  | "Blocked"
  | "Complete";

export interface AttentionDetails {
  state: AttentionState;
  reason?: string;
}

export interface OrderInvoiceSummary {
  invoiceCount: number;
  invoiced: number;
  cleared: number;
  outstanding: number;
}

export function formatCurrency(payload: { value: number | undefined | null }): string {
  const { value } = payload;
  return `$${(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;
}

export function formatNumber(payload: { value: number | undefined | null }): string {
  const { value } = payload;
  return (value || 0).toLocaleString();
}

export function abbreviateProductName(name: string): string {
  const airlinePart = name.split("/")[0].trim();
  return airlinePart
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

// Shortens a full document ID to the same 8-character form shown in the
// operations tables, so exports match what users see on screen.
export function formatShortId(value: string | undefined): string {
  return value?.slice(0, 8) || "Unlinked";
}

// Resolves a display product code, falling back to an abbreviation of the
// product name when the product has no explicit code.
export function resolveProductCode(payload: {
  productCode?: string | null;
  productName: string;
}): string {
  const { productCode, productName } = payload;
  return productCode || abbreviateProductName(productName);
}

export function getClearedInvoiceAmountsById(payload: {
  payments: Pick<
    Payment,
    | "appliesToId"
    | "appliesToType"
    | "amount"
    | "status"
    | "direction"
    | "source"
  >[];
}): Map<string, number> {
  const clearedByInvoiceId = new Map<string, number>();

  payload.payments.forEach((payment) => {
    if (
      payment.appliesToType !== "invoice" ||
      payment.status !== "cleared" ||
      payment.direction === "incoming" ||
      // Sourced payments (PayPal sync) are context-only, not counted.
      payment.source
    ) {
      return;
    }

    clearedByInvoiceId.set(
      payment.appliesToId,
      (clearedByInvoiceId.get(payment.appliesToId) || 0) + payment.amount,
    );
  });

  return clearedByInvoiceId;
}

export function getOrderInvoiceSummary(payload: {
  kind: "po" | "so";
  orderId?: string;
  invoices: Pick<Invoice, "id" | "poId" | "soId" | "amountDue">[];
  clearedByInvoiceId?: ReadonlyMap<string, number>;
}): OrderInvoiceSummary {
  const { kind, orderId, invoices, clearedByInvoiceId } = payload;

  if (!orderId) {
    return {
      invoiceCount: 0,
      invoiced: 0,
      cleared: 0,
      outstanding: 0,
    };
  }

  const linkedInvoices = invoices.filter((invoice) =>
    kind === "po" ? invoice.poId === orderId : invoice.soId === orderId,
  );
  const invoiced = linkedInvoices.reduce((sum, invoice) => sum + invoice.amountDue, 0);
  const cleared = linkedInvoices.reduce(
    (sum, invoice) => sum + (clearedByInvoiceId?.get(invoice.id) || 0),
    0,
  );

  return {
    invoiceCount: linkedInvoices.length,
    invoiced,
    cleared,
    outstanding: Math.max(0, invoiced - cleared),
  };
}

const STATUS_LABEL_MAP: Record<string, string> = {
  po: "PO",
  so: "SO",
  "2fa": "2FA",
  cpm: "CPM",
  cogs: "COGS",
  otp: "OTP",
  ah: "AH",
};

const FULL_VALUE_LABEL_MAP: Record<string, string> = {
  paypal_goods_services: "PayPal - Goods and Services",
  paypal_friends_family: "PayPal - Friends and Family",
  paypal_goods_services_fees: "PayPal - Goods and Services + Fees",
  paypal: "PayPal",
};

/** Turns a work email like "bryan.smith@…" into a "Bryan Smith" label. */
export function formatUserDisplayName(email: string | undefined): string {
  if (!email) {
    return "Unknown user";
  }

  const localPart = email.split("@")[0] || "";
  const normalized = localPart.replace(/[._-]+/g, " ").trim();

  if (!normalized) {
    return email;
  }

  return normalized
    .split(/\s+/)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(" ");
}

export function formatStatusLabel(value: string | undefined | null): string {
  if (!value) return "-";

  const trimmed = String(value).trim();
  if (FULL_VALUE_LABEL_MAP[trimmed]) return FULL_VALUE_LABEL_MAP[trimmed];

  const normalized = trimmed
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();

  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => STATUS_LABEL_MAP[token] || `${token.charAt(0).toUpperCase()}${token.slice(1)}`)
    .join(" ");
}

export function getInventoryOperationalStatus(payload: {
  inventory: Pick<Inventory, "status" | "availableMiles" | "bookingBlockedUntil">;
  now?: Date;
}): Inventory["status"] {
  const { inventory, now = new Date() } = payload;

  if (inventory.status === "archived") {
    return "archived";
  }

  const bookingBlockedUntil = timestampToDate(inventory.bookingBlockedUntil);

  if (!bookingBlockedUntil) {
    return inventory.status;
  }

  if (bookingBlockedUntil.getTime() > now.getTime()) {
    return "blocked";
  }

  if (inventory.status === "draft" || inventory.status === "closed") {
    return inventory.status;
  }

  return inventory.availableMiles > 0 ? "active" : "depleted";
}

export function isInventoryBlockedForSalesOrderLinking(payload: {
  inventory: Pick<Inventory, "status" | "availableMiles" | "bookingBlockedUntil">;
  now?: Date;
}): boolean {
  return getInventoryOperationalStatus(payload) === "blocked";
}

export function getInventoryBookingBlockDetails(payload: {
  inventory: Pick<
    Inventory,
    "bookingBlockedUntil" | "bookingBlockedBySalesOrderId" | "bookingBlockedTbd"
  >;
  now?: Date;
}): {
  blockedUntil: Date | null;
  blockingSalesOrderId: string | null;
  tbd: boolean;
} | null {
  const { inventory, now = new Date() } = payload;
  const bookingBlockedUntil = timestampToDate(inventory.bookingBlockedUntil);
  const tbd = inventory.bookingBlockedTbd === true;

  if (
    !bookingBlockedUntil ||
    bookingBlockedUntil.getTime() <= now.getTime()
  ) {
    return null;
  }

  return {
    blockedUntil: bookingBlockedUntil,
    blockingSalesOrderId: inventory.bookingBlockedBySalesOrderId || null,
    tbd,
  };
}

export function getSuggestionAttention(payload: {
  status: string;
  candidateType: "po" | "so";
  hasCompleteAccount?: boolean;
  allocationPossible?: boolean;
}): AttentionState {
  return getSuggestionAttentionDetails(payload).state;
}

export function getSuggestionAttentionDetails(payload: {
  status: string;
  candidateType: "po" | "so";
  hasCompleteAccount?: boolean;
  allocationPossible?: boolean;
}): AttentionDetails {
  const { status, candidateType, hasCompleteAccount, allocationPossible } = payload;

  if (status === "converted") {
    return {
      state: "Complete",
      reason: "This suggestion has already been converted into an order.",
    };
  }

  if (status === "invalidated" || status === "rejected") {
    return {
      state: "Blocked",
      reason:
        status === "rejected"
          ? "This suggestion was rejected and cannot be actioned."
          : "Source deal data changed, so this suggestion is no longer valid.",
    };
  }

  if (candidateType === "po" && hasCompleteAccount === false) {
    return {
      state: "Needs Action",
      reason: "Complete the inventory credentials before proceeding.",
    };
  }

  if (candidateType === "so" && allocationPossible === false) {
    return {
      state: "Blocked",
      reason: "Current inventory cannot fulfill this suggestion.",
    };
  }

  return { state: "Ready" };
}

/**
 * Cancelled and bought-back POs share the terminal `voided` status so that payment
 * matching and reporting only ever have one dead-PO case to skip. Readers still need
 * to tell them apart, so widen the stored status into a display status token — pass
 * the result through `formatStatusLabel` (or straight to a component that does).
 * POs voided before `voidType` was tracked were all buybacks.
 */
export function getPurchaseOrderDisplayStatus(payload: {
  status: string;
  voidType?: string;
}): string {
  if (payload.status === "voided" && payload.voidType === "cancelled") {
    return "cancelled";
  }
  return payload.status;
}

export function getPurchaseOrderAttention(payload: {
  status: string;
  paymentStatus: string;
  voidType?: string;
  accountNeedsAction?: boolean;
}): AttentionState {
  return getPurchaseOrderAttentionDetails(payload).state;
}

export function getPurchaseOrderAttentionDetails(payload: {
  status: string;
  paymentStatus: string;
  voidType?: string;
  accountNeedsAction?: boolean;
}): AttentionDetails {
  const { status, paymentStatus, voidType, accountNeedsAction } = payload;

  if (status === "voided") {
    return {
      state: "Complete",
      // POs voided before `voidType` was tracked were all buybacks.
      reason:
        voidType === "cancelled"
          ? "This purchase order was cancelled."
          : "This purchase order has been bought back and voided.",
    };
  }

  if (status === "blocked") {
    return {
      state: "Blocked",
      reason: "This purchase order is blocked and requires manual review.",
    };
  }

  if (status === "closed" && paymentStatus === "paid") {
    return {
      state: "Complete",
      reason: "This purchase order is closed and fully paid.",
    };
  }

  if (accountNeedsAction) {
    return {
      state: "Needs Action",
      reason: "The inventory credentials are incomplete and need attention.",
    };
  }

  if (paymentStatus !== "paid") {
    return {
      state: "Needs Action",
      reason:
        paymentStatus === "partially_paid"
          ? "Payment is only partially recorded. Record the remaining amount."
          : "No cleared payment is recorded for this purchase order yet.",
    };
  }

  return { state: "Ready" };
}

export const SALES_ORDER_DRAFT_NOTICE =
  "Awaiting admin approval because it's for more miles than the inventory has available. Its miles are taken from inventory once an admin approves it.";

export interface SalesOrderShortfall {
  requiredMiles: number;
  availableMiles: number;
  shortfallMiles: number;
}

/** The miles an order would oversell its inventory by, or null when it fits. */
export function getSalesOrderShortfall(payload: {
  requiredMiles: number;
  availableMiles: number | undefined;
}): SalesOrderShortfall | null {
  const { requiredMiles } = payload;
  const availableMiles = payload.availableMiles ?? 0;
  return requiredMiles > availableMiles
    ? {
        requiredMiles,
        availableMiles,
        shortfallMiles: requiredMiles - availableMiles,
      }
    : null;
}

/**
 * Warns before submitting an oversell: approvers will be asked to confirm it,
 * anyone else's order goes to an admin for approval.
 */
export function getSalesOrderOversellNotice(payload: {
  shortfall: SalesOrderShortfall;
  canApprove: boolean;
}): string {
  const { shortfall, canApprove } = payload;
  const overBy = `This is ${shortfall.shortfallMiles.toLocaleString()} miles more than the ${shortfall.availableMiles.toLocaleString()} available`;

  return canApprove
    ? `${overBy}. You'll be asked to confirm the oversell, which takes this inventory's available miles below zero.`
    : `${overBy}, so it will be sent to an admin for approval. No miles are taken from inventory until it's approved.`;
}

/** The shortfall the server reported when it refused an oversell, if that was the error. */
export function getSalesOrderShortfallFromError(
  error: unknown,
): SalesOrderShortfall | null {
  const { details } = (error ?? {}) as {
    details?: Partial<SalesOrderShortfall> & { code?: string };
  };

  return details?.code === "INSUFFICIENT_INVENTORY" &&
    details.requiredMiles !== undefined &&
    details.availableMiles !== undefined &&
    details.shortfallMiles !== undefined
    ? {
        requiredMiles: details.requiredMiles,
        availableMiles: details.availableMiles,
        shortfallMiles: details.shortfallMiles,
      }
    : null;
}

/** Turns a failed sell order create/update call into a message the user can act on. */
export function getSalesOrderErrorMessage(error: unknown): string {
  const { message, details } = (error ?? {}) as {
    message?: string;
    details?: {
      code?: string;
      requiredMiles?: number;
      availableMiles?: number;
      shortfallMiles?: number;
    };
  };

  switch (details?.code) {
    case "INSUFFICIENT_INVENTORY":
      return `Not enough miles in this inventory: ${details.requiredMiles?.toLocaleString()} miles needed but only ${details.availableMiles?.toLocaleString()} available (${details.shortfallMiles?.toLocaleString()} short).`;
    case "INVENTORY_BLOCKED":
      return "This inventory is currently blocked for another sell order.";
    case "SALES_ORDER_INVALID":
    case "SALES_ORDER_APPROVAL_REQUIRED":
      if (message) {
        return message;
      }
  }

  return "Something went wrong. Please try again.";
}

export function getSalesOrderAttention(payload: {
  status: string;
  invoiceCount: number;
}): AttentionState {
  return getSalesOrderAttentionDetails(payload).state;
}

export function getSalesOrderAttentionDetails(payload: {
  status: string;
  invoiceCount: number;
}): AttentionDetails {
  const { status, invoiceCount } = payload;

  if (status === "draft") {
    return {
      state: "Awaiting Approval",
      reason: SALES_ORDER_DRAFT_NOTICE,
    };
  }

  if (status === "cancelled") {
    return {
      state: "Blocked",
      reason: "This sales order is cancelled.",
    };
  }

  if (status === "delivered") {
    return {
      state: "Complete",
      reason: "This sales order has been delivered.",
    };
  }

  if (invoiceCount === 0) {
    return {
      state: "Needs Action",
      reason: "No invoice has been created for this sales order.",
    };
  }

  if (status === "allocated" || status === "open_booking") {
    return {
      state: "At Risk",
      reason:
        status === "open_booking"
          ? "Order is in open booking and may be exposed to execution delays."
          : "Order is allocated but still operationally exposed until fully delivered.",
    };
  }

  return { state: "Ready" };
}

export function getInvoiceAttention(payload: {
  status: string;
  outstandingAmount: number;
  isOverdue: boolean;
}): AttentionState {
  return getInvoiceAttentionDetails(payload).state;
}

export function getInvoiceAttentionDetails(payload: {
  status: string;
  outstandingAmount: number;
  isOverdue: boolean;
}): AttentionDetails {
  const { status, outstandingAmount, isOverdue } = payload;

  if (status === "paid" || outstandingAmount <= 0) {
    return {
      state: "Complete",
      reason: "Invoice is fully settled.",
    };
  }

  if (status === "void" || status === "refunded") {
    return {
      state: "Blocked",
      reason: status === "void" ? "Invoice is void and inactive." : "Invoice has been refunded.",
    };
  }

  if (isOverdue) {
    return {
      state: "At Risk",
      reason: "Invoice is overdue and still has an outstanding balance.",
    };
  }

  return {
    state: "Needs Action",
    reason: "Outstanding balance remains. Record payment or update invoice status.",
  };
}

export function buildInventoryWorkspacePath(payload: {
  inventoryId?: string;
  purchaseOrderId?: string;
  salesOrderId?: string;
  invoiceId?: string;
  vendorId?: string;
  clientId?: string;
  focus?:
    | "purchase-orders"
    | "purchase-financials"
    | "inventory"
    | "sell-orders"
    | "sell-financials"
    | "audit-log";
}): string {
  const params = new URLSearchParams();

  if (payload.inventoryId) {
    params.set("inventoryId", payload.inventoryId);
  }

  if (payload.purchaseOrderId) {
    params.set("purchaseOrderId", payload.purchaseOrderId);
  }

  if (payload.salesOrderId) {
    params.set("salesOrderId", payload.salesOrderId);
  }

  if (payload.invoiceId) {
    params.set("invoiceId", payload.invoiceId);
  }

  if (payload.vendorId) {
    params.set("vendorId", payload.vendorId);
  }

  if (payload.clientId) {
    params.set("clientId", payload.clientId);
  }

  if (payload.focus) {
    params.set("focus", payload.focus);
  }

  return `/operations/inventory-workspace${params.toString() ? `?${params.toString()}` : ""}`;
}

export function buildSupplyWorkspacePath(payload: {
  inventoryId?: string;
  poId?: string;
}): string {
  return buildInventoryWorkspacePath({
    inventoryId: payload.inventoryId,
    purchaseOrderId: payload.poId,
    focus: "purchase-orders",
  });
}

export function buildDemandWorkspacePath(payload: {
  inventoryId?: string;
  soId?: string;
  invoiceId?: string;
}): string {
  return buildInventoryWorkspacePath({
    inventoryId: payload.inventoryId,
    salesOrderId: payload.soId,
    invoiceId: payload.invoiceId,
    focus: "sell-orders",
  });
}
